import { useEffect, useState } from "react";
import {
  Category,
  Shop,
  StockLargeClassification,
  Storehouse,
  StorehouseSupplierOptionsWithBlank,
  Supplier,
  TyreOptions,
  WheelOptions,
} from "../types";
import { categories } from "../constants";
import getTyreOptionsApi from "../api/getTyreOptionsApi";

/**
 * 在庫検索hooks
 * @param needStoreOptions 倉庫、仕入先のプルダウンも取得するかどうか
 * @param defaultStorehouse デフォルトの倉庫名
 * @param defaultSupplier デフォルトの仕入先名
 * @param classificationTireOnly 大分類がタイヤホイールのみかどうか
 * @returns JSX.Element
 */
export default function useStockSearch(needStoreOptions: boolean, defaultStorehouse?: string | null, defaultSupplier?: string | null, classificationTireOnly?: boolean) {
  const defaultStorehouseSupplier: StorehouseSupplierOptionsWithBlank = (() => {
    if (defaultStorehouse) return "倉庫";
    if (defaultSupplier) return "仕入先";
    return "";
  })();
  const [productCd, setProductCd] = useState("");
  const [productName, setProductName] = useState("");
  const [brand, setBrand] = useState("");
  const [JANCd, setJANCd] = useState("");
  const [productNumber, setProductNumber] = useState("");
  const [tyreWidth, setTyreWidth] = useState<number | undefined>(undefined);
  const [tyreRatio, setTyreRatio] = useState<number | undefined>(undefined);
  const [tyreRimDiam, setTyreRimDiam] = useState<number | undefined>(undefined);
  const [wheelWidth, setWheelWidth] = useState<number | undefined>(undefined);
  const [wheelPcd, setWheelPcd] = useState<number | undefined>(undefined);
  const [wheelHole, setWheelHole] = useState<number | undefined>(undefined);
  const [wheelRimDiam, setWheelRimDiam] = useState<number | undefined>(undefined);
  const companyCd: string | undefined = localStorage.getItem("company_cd") ?? undefined; // ToDo: ローカルストレージから取得
  const [shopCd, setShopCd] = useState<string>(localStorage.getItem("shop_cd") ?? ""); // ToDo: ローカルストレージから取得
  const [classification, setClassification] = useState("");
  const [storehouseSupplierWithBlank, setStorehouseSupplierWithBlank] =
    useState<StorehouseSupplierOptionsWithBlank>(defaultStorehouseSupplier);
  const [storehouse, setStorehouse] = useState(defaultStorehouse ?? "");
  const [supplier, setSupplier] = useState(defaultSupplier ?? ""); // 仕入先名
  const [tyreOptions, setTyreOptions] = useState<TyreOptions>({
    tyreWidthList: [],
    tyreRatioList: [],
    tyreRimDiamList: [],
  });
  const [wheelOptions, setWheelOptions] = useState<WheelOptions>({
    wheelWidthList: [],
    wheelPcdList: [],
    wheelHoleList: [],
    wheelRimDiamList: [],
  });
  const [classificationOptions, setClassificationOptions] = useState<StockLargeClassification[]>(
    [],
  );
  const [storehouseOptions, setStorehouseOptions] = useState<Storehouse[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [shopOptions, setShopOptions] = useState<Shop[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState<Category>(categories[1]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const data = await getTyreOptionsApi(needStoreOptions, companyCd ?? undefined);
        setTyreOptions(data.tyreOptions);
        setWheelOptions(data.wheelOptions);
        setClassificationOptions(
          classificationTireOnly
            ? data.classificationOptions.filter(co => co.id === '02' || co.id === '03') // タイヤ・ホイールのみ
            : data.classificationOptions
        );
        setBrandOptions(data.brandOptions);
        if (needStoreOptions) {
          setStorehouseOptions(data.storehouseOptions);
        }
        if (companyCd) {
          // 混合だとしても得意先は扱わない。
          setShopOptions(data.shopOptions.filter((so => so.shopCd != null)));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return [
    {
      productCd,
      productName,
      brand,
      JANCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplierWithBlank,
      storehouse,
      supplier,
      category,
      shopCd,
      companyCd,
    },
    {
      setProductCd,
      setProductName,
      setBrand,
      setJANCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setStorehouseSupplierWithBlank,
      setStorehouse,
      setSupplier,
      setCategory,
      setShopCd,
    },
    {
      classificationOptions,
      tyreOptions,
      wheelOptions,
      storehouseOptions,
      brandOptions,
      shopOptions,
    },
    { isLoading },
  ] as const;
}
